import { ref, computed } from 'vue'
import SiteAdminService from '@/services/SiteAdminService'
import { itemRef, productsCovering, nextPlanItems, sameRefs } from '@/Utils/siteAccess'
import { notifySite, plansLink } from '@/Utils/siteToast'
import { premiumBadgeLabel as badgeLabelFor } from '@/components/Sites/siteDisplayOptions'

/**
 * Shared loaders for one site's management pages: categories, content,
 * plans (paywall products) and settings, plus the plan-membership sync used by
 * the per-item access control (access model v2: items only). Errors are
 * returned, not thrown, so pages can render partial data (e.g. plans 503
 * while the paywall link is pending).
 */
export function useSiteAdmin(siteId) {
	const categories = ref([])
	const content = ref([])
	const plans = ref([])
	const settings = ref(null)
	// the rest of the runtime `GET /settings` answer: seeded, writableKeys, draft, draftCount, draftUpdatedAt
	const settingsMeta = ref(null)
	// castr GET /sites/:id/monetization (also piggybacks on GET /plans)
	const monetization = ref(null)
	// castr GET /plans `capabilities` (§6h): `recurring` is the SITES_RECURRING_PLANS
	// flag — subscriptions are deferred, so absent counts as off.
	const planCapabilities = ref({ recurring: false })
	const errors = ref({})
	const loading = ref({ categories: false, content: false, plans: false, settings: false, monetization: false })

	const id = () => (typeof siteId === 'function' ? siteId() : siteId.value ?? siteId)

	async function load(key, fn) {
		loading.value = { ...loading.value, [key]: true }
		try {
			const result = await fn()
			errors.value = { ...errors.value, [key]: null }
			return result
		} catch (err) {
			errors.value = { ...errors.value, [key]: err }
			return null
		} finally {
			loading.value = { ...loading.value, [key]: false }
		}
	}

	const loadCategories = () => load('categories', async () => {
		const res = await SiteAdminService.listCategories(id())
		categories.value = res?.items || []
		return categories.value
	})
	const loadContent = (params) => load('content', async () => {
		const res = await SiteAdminService.listContent(id(), params)
		content.value = res?.items || []
		return content.value
	})
	const loadPlans = () => load('plans', async () => {
		const res = await SiteAdminService.listPlans(id())
		plans.value = res?.products || []
		planCapabilities.value = { recurring: res?.capabilities?.recurring === true }
		if (res?.stripe) {
			monetization.value = { ...(monetization.value || {}), stripe: res.stripe, canMonetize: res.canMonetize === true, monetization: res.monetization || monetization.value?.monetization || null }
		}
		return plans.value
	})
	const loadSettings = () => load('settings', async () => {
		const res = await SiteAdminService.getSettings(id())
		settings.value = res?.settings || {}
		settingsMeta.value = res && typeof res === 'object' ? { ...res, settings: undefined } : null
		return settings.value
	})
	const loadMonetization = () => load('monetization', async () => {
		monetization.value = await SiteAdminService.getMonetization(id())
		return monetization.value
	})

	const sellablePlans = computed(() => plans.value.filter((p) => p.kind !== 'grant'))
	const activePlans = computed(() => sellablePlans.value.filter((p) => p.active !== false))

	// Stripe Connect gate. Unknown (not loaded yet / castr unreachable) counts
	// as connected here so the UI does not block on a transient error — castr
	// refuses the write with `stripe_not_connected` anyway.
	const stripe = computed(() => monetization.value?.stripe || null)
	const canMonetize = computed(() => (monetization.value ? monetization.value.canMonetize === true : true))
	const monetizationPaused = computed(() => monetization.value?.monetization?.status === 'paused')
	const pausedPlanIds = computed(() => new Set(monetization.value?.monetization?.pausedProducts || []))

	/**
	 * Effective Theme › Player "Open the player immediately" setting: the saved
	 * value when `GET /settings` has one, else the runtime default (true). Seeds
	 * the per-item toggle of a new content item.
	 */
	const playerOpenImmediately = computed(() => {
		const v = settings.value?.playerOpenImmediately
		if (typeof v === 'boolean') return v
		if (v === 1 || v === '1' || v === 'true') return true
		if (v === 0 || v === '0' || v === 'false') return false
		return true
	})

	/**
	 * Theme › Player badge text for paid items, as the Theme page and its live
	 * preview show it (published ⊕ draft): custom text, "Premium", or "Paid"
	 * when the badge is off. Trello gudiDap3: the Library card preview said
	 * "Paid" whatever the owner had set.
	 */
	const premiumBadgeLabel = computed(() => {
		const draft = settingsMeta.value?.draft
		return badgeLabelFor({ ...(settings.value || {}), ...(draft && typeof draft === 'object' ? draft : {}) })
	})

	/** Active plans that unlock this item: all-access ones plus `scope: items` plans mapped to `item:<id>`. */
	const plansCoveringItem = (item) => productsCovering(activePlans.value, [itemRef(item.id)])
	/** `scope: items` plans the owner can map an item into. */
	const itemPlans = computed(() => activePlans.value.filter((p) => p.scope === 'items'))
	const allAccessPlans = computed(() => activePlans.value.filter((p) => p.scope === 'site'))

	/** `scope: items` plans whose mapping currently lists `ref`. */
	const planIdsForRef = (ref) => activePlans.value
		.filter((p) => p.scope === 'items' && (p.items || []).includes(ref))
		.map((p) => p.public_id)

	/**
	 * Make exactly `selectedIds` (scope:items plans) carry the given ref(s);
	 * every other scope:items plan drops them. One PUT per plan whose list
	 * actually changes (an unchanged list is never sent), castr mirrors the
	 * mapping to the runtime. Never throws: each plan is reported in the result
	 * so the caller can tell the owner what happened after the access change
	 * itself already succeeded.
	 * @param {string|string[]} refs `item:<id>` ref or refs
	 * @returns {Promise<{ changed: object[], emptied: object[], failed: { plan: object, error: Error, emptied: boolean }[] }>}
	 *   `emptied`: plans castr accepted with `warning: plan_has_no_items` (or that now
	 *   list nothing); `failed`: plans whose PUT was refused (`emptied` when the
	 *   refusal is the paywall's "at least one item" 422 on an empty list).
	 */
	async function applyPlanMembership(refs, selectedIds) {
		const wanted = new Set(selectedIds || [])
		const result = { changed: [], emptied: [], failed: [] }
		for (const plan of activePlans.value) {
			if (plan.scope !== 'items') continue
			const items = plan.items || []
			const next = nextPlanItems(plan, refs, wanted.has(plan.public_id))
			if (sameRefs(items, next)) continue
			try {
				const { warning, runtimeSync, ...updated } = (await SiteAdminService.replacePlanItems(id(), plan.public_id, next)) || {}
				const merged = { ...plan, ...updated, items: Array.isArray(updated.items) ? updated.items : next }
				plans.value = plans.value.map((p) => (p.public_id === plan.public_id ? { ...p, ...merged } : p))
				result.changed.push(merged)
				if (warning === 'plan_has_no_items' || merged.items.length === 0) result.emptied.push(merged)
			} catch (error) {
				result.failed.push({ plan, error, emptied: next.length === 0 && (error?.code === 422 || !error?.code) })
			}
		}
		return result
	}

	/**
	 * Toasts for an `applyPlanMembership` result — the upstream message is never
	 * shown. Returns false when a plan could not be updated.
	 * @param {{ emptied: object[], failed: object[] }} result
	 * @param {{ done?: string }} [opts] what already succeeded ("Access changed" / "Content saved")
	 */
	function reportPlanMembership(result, { done = 'Access changed' } = {}) {
		if (!result) return true
		const link = plansLink(id())
		for (const plan of result.emptied || []) {
			notifySite({ type: 'info', text: `Plan “${plan.title}” no longer unlocks any content — edit the plan or archive it.`, link })
		}
		for (const { plan, emptied } of result.failed || []) {
			notifySite({
				type: 'error',
				text: emptied
					? `${done}, but plan “${plan.title}” could not be updated — it would be left with no content. Edit or archive it under Plans.`
					: `${done}, but plan “${plan.title}” could not be updated. Check it under Plans.`,
				link,
			})
		}
		return !(result.failed || []).length
	}

	function upsertPlan(product) {
		const idx = plans.value.findIndex((p) => p.public_id === product.public_id)
		if (idx >= 0) plans.value.splice(idx, 1, { ...plans.value[idx], ...product })
		else plans.value.unshift(product)
	}

	return {
		categories,
		content,
		plans,
		settings,
		settingsMeta,
		monetization,
		planCapabilities,
		errors,
		loading,
		sellablePlans,
		activePlans,
		itemPlans,
		allAccessPlans,
		stripe,
		canMonetize,
		monetizationPaused,
		pausedPlanIds,
		playerOpenImmediately,
		premiumBadgeLabel,
		loadCategories,
		loadContent,
		loadPlans,
		loadSettings,
		loadMonetization,
		plansCoveringItem,
		planIdsForRef,
		applyPlanMembership,
		reportPlanMembership,
		upsertPlan,
		itemRef,
	}
}
